/**
 * The generator column.
 *
 * Rows are built once and then only ever updated by assigning to textContent
 * on cached nodes. Nothing here rebuilds innerHTML on tick.
 */

import type Decimal from 'break_infinity.js';
import { GENERATORS } from '../../data/generators.ts';
import { MILESTONE_EVERY } from '../../data/balance.ts';
import {
  bestBuy,
  bulkCount,
  costOf,
  efficiency,
  generatorUnlockKey,
  milestoneMult,
  feedShortfall,
  roomToBuy,
  tierUnlocked,
  unitsToUnlock,
  supportState,
  nextMilestoneMult,
  tierTPS,
  unitsToMilestone,
  type Modifiers,
} from '../../sim/formulas.ts';
import type { GameState } from '../../sim/state.ts';
import { formatInt, formatNumber, formatRate } from '../format.ts';
import { setDisabled, setText } from '../dom.ts';

export type BulkMode = number | 'max';

interface Row {
  wrap: HTMLElement;
  button: HTMLButtonElement;
  name: HTMLElement;
  count: HTMLElement;
  cost: HTMLElement;
  rate: HTMLElement;
  mult: HTMLElement;
  value: HTMLElement;
  milestone: HTMLElement;
  support: HTMLElement;
  bar: HTMLElement;
  autoRow: HTMLElement;
  autoInput: HTMLInputElement;
  revealed: boolean;
}

export interface GeneratorPanel {
  update(state: GameState, mods: Modifiers, bulk: BulkMode): void;
}

export interface GeneratorHandlers {
  onBuy(index: number): void;
  onAutoBuyChange(generatorId: string, enabled: boolean): void;
}

export function createGeneratorPanel(
  mount: HTMLElement,
  handlers: GeneratorHandlers,
): GeneratorPanel {
  const rows: Row[] = GENERATORS.map((def, index) => {
    // The auto toggle is a sibling of the row, not a child: a checkbox inside
    // a button is invalid, and the wrapper lets it sit in the row's corner
    // instead of costing another line of height.
    const wrap = document.createElement('div');
    wrap.className = 'gen-wrap';
    wrap.hidden = true;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gen';

    const name = span('gen__name', def.name);
    const count = span('gen__count', '0');
    const cost = span('gen__cost', '');
    const rate = span('gen__rate', '');
    const mult = span('gen__mult', '');
    const value = span('gen__value', '');
    const role = span('gen__role', def.role);

    // Milestone progress: the reason to keep buying this particular row.
    const track = document.createElement('span');
    track.className = 'gen__track';
    const bar = document.createElement('span');
    bar.className = 'gen__bar';
    track.append(bar);
    const milestone = span('gen__milestone', '');
    const support = span('gen__support', '');

    button.append(name, count, mult, cost, rate, role, track, milestone, value, support);
    button.addEventListener('click', () => handlers.onBuy(index));

    const autoRow = document.createElement('div');
    autoRow.className = 'autobuy-row';
    autoRow.hidden = true;
    const autoInput = document.createElement('input');
    autoInput.type = 'checkbox';
    autoInput.id = `autobuy-${def.id}`;
    const autoLabel = document.createElement('label');
    autoLabel.htmlFor = autoInput.id;
    autoLabel.textContent = 'Auto';
    autoInput.addEventListener('change', () =>
      handlers.onAutoBuyChange(def.id, autoInput.checked),
    );
    autoRow.append(autoInput, autoLabel);

    wrap.append(button, autoRow);
    mount.append(wrap);
    return {
      wrap, button, name, count, cost, rate, mult, value, milestone, support, bar,
      autoRow, autoInput, revealed: false,
    };
  });

  function update(state: GameState, mods: Modifiers, bulk: BulkMode): void {
    const format = state.settings.numberFormat;
    const autobuyUnlocked = mods.flags.autobuy === true;
    const best = bestBuy(state);
    const bestValue = best >= 0 ? efficiency(state, best) : 0;

    for (let i = 0; i < GENERATORS.length; i++) {
      const def = GENERATORS[i];
      const row = rows[i];
      if (def === undefined || row === undefined) continue;

      const visible = state.unlocks[generatorUnlockKey(def)] === true;
      if (!visible) {
        if (!row.wrap.hidden) row.wrap.hidden = true;
        continue;
      }

      if (row.wrap.hidden) {
        row.wrap.hidden = false;
        row.wrap.classList.add('is-revealing');
      }
      if (!row.revealed) row.revealed = true;

      const owned = state.generators[i] ?? 0;
      const count = bulkCount(state, i, bulk);
      // Max-buy with nothing affordable still has to quote a price: show one.
      const quoted = count > 0 ? count : 1;
      const price: Decimal = costOf(def, owned, quoted);
      const unlocked = tierUnlocked(state, i);
      const room = roomToBuy(state, i);
      const affordable = count > 0 && state.thoughts.gte(price);

      setText(row.count, formatInt(owned));
      setText(
        row.cost,
        `${count > 0 ? `x${formatInt(count)}` : 'x1'}  ${formatNumber(price, format)}`,
      );
      setText(row.rate, owned > 0 ? formatRate(tierTPS(state, i, mods), format) : '—');
      setDisabled(row.button, !affordable);
      row.button.classList.toggle('is-owned', owned > 0);

      // Milestone: current bonus, distance to the next one, and a filled bar.
      const bonus = milestoneMult(owned);
      setText(row.mult, bonus.gt(1) ? `x${formatNumber(bonus, format)}` : '');
      const remaining = unitsToMilestone(owned);
      setText(
        row.milestone,
        `${formatInt(remaining)} more to x${formatNumber(nextMilestoneMult(owned), format)}`,
      );
      const progress = `${(((MILESTONE_EVERY - remaining) / MILESTONE_EVERY) * 100).toFixed(0)}%`;
      if (row.bar.style.width !== progress) row.bar.style.width = progress;

      // Support: how much of this tier the tier above is actually backing.
      const sup = supportState(state, i);
      const feeder = GENERATORS[i - 1];
      if (!unlocked && feeder !== undefined) {
        const short = unitsToUnlock(state, i);
        setText(
          row.support,
          `Locked — needs ${formatInt(short)} more ${feeder.name}${short === 1 ? '' : 's'}`,
        );
      } else if (room <= 0 && feeder !== undefined) {
        const short = feedShortfall(state, i);
        setText(
          row.support,
          `Full — ${formatInt(short)} more ${feeder.name}${short === 1 ? '' : 's'} to add another`,
        );
      } else if (owned === 0 || feeder === undefined || (def.needs ?? 0) <= 0) {
        setText(row.support, '');
      } else if (sup.stranded > 0) {
        const shortfall = sup.stranded * (def.needs ?? 1);
        setText(
          row.support,
          `${formatInt(sup.stranded)} starving — ${formatInt(shortfall)} more ` +
            `${feeder.name}${shortfall === 1 ? '' : 's'}`,
        );
      } else {
        setText(row.support, `Fed ${formatInt(owned)} / ${formatInt(sup.capacity)}`);
      }
      row.support.classList.toggle('is-stranded', sup.stranded > 0 || room <= 0);
      row.button.classList.toggle('is-locked-tier', room <= 0);

      // Value: how this row compares to the best row available, so the player
      // never has to divide output by price in their head.
      const value = efficiency(state, i);
      const relative = bestValue > 0 ? value / bestValue : 0;
      row.button.classList.toggle('is-best', i === best);
      setText(
        row.value,
        i === best
          ? 'Best value'
          : relative > 0
            ? `${relative >= 0.1 ? relative.toFixed(2) : relative.toPrecision(1)}x value`
            : '',
      );

      const showAuto = autobuyUnlocked && owned > 0;
      if (row.autoRow.hidden === showAuto) row.autoRow.hidden = !showAuto;
      const enabled = state.settings.autoBuy[def.id] === true;
      if (row.autoInput.checked !== enabled) row.autoInput.checked = enabled;
    }
  }

  return { update };
}

function span(className: string, text: string): HTMLElement {
  const node = document.createElement('span');
  node.className = className;
  node.textContent = text;
  return node;
}
